import { DEFAULT_CHART_LIMIT } from '@omnea-app/packages/universal/reporting/widgetLimits'
import { type FilterOperator } from './filterOperators'
import { type TimeGranularity } from './granularity'
import { type ChartType, type WidgetTimeRange } from './insightsTypes'

// values: null = all (no filter), [] = none (in-progress), [a, b] = narrow to those.
// null + partial selections persist; [] is stripped on save (see isCompleteFilter).
// With an operator, values are the numbers it compares against as plain decimal strings ('' = not yet entered).
export type WidgetFilter = { breakdown: string; values: string[] | null; operator?: FilterOperator }

// Bounds are always recorded, even where they match the data's own edges: a saved entry that left them
// off would re-derive them from whatever the data spans at load, so the width would drift as data grew.
export type NumericBucketEntry = { kind: 'number'; interval: number; min: number; max: number }

// Only an explicit pick is stored; with no entry the grouping follows the range and axis. A pick is
// coarsened at query time and never rewritten, so it comes back once the range can chart it again.
type DateBucketEntry = { kind: 'date'; granularity: TimeGranularity }

export type BreakdownBucketEntry = NumericBucketEntry | DateBucketEntry

export const numericBucketEntry = (entry: BreakdownBucketEntry | undefined): NumericBucketEntry | undefined =>
  entry?.kind === 'number' ? entry : undefined

export const savedDateGranularity = (entry: BreakdownBucketEntry | undefined): TimeGranularity | null =>
  entry?.kind === 'date' ? entry.granularity : null

export const normalizeWidgetTimeRange = (timeRange: WidgetTimeRange | { start: string; end: string }): WidgetTimeRange =>
  typeof timeRange === 'object' && !('preset' in timeRange) ? { preset: 'custom', ...timeRange } : timeRange

export type WidgetConfig = {
  title: string
  subtitle?: string
  source: string
  metricGroup: Record<string, string[]>
  breakdowns?: string[]
  /** @deprecated read fallback only; the backend dual-writes it as breakdowns[0]. Use resolveBreakdowns. */
  breakdown?: string | null
  chartType: ChartType
  timeRange: WidgetTimeRange
  granularity?: TimeGranularity | null
  /**
   * Top-N segments rendered in the chart only; tables show everything. Absent → DEFAULT_CHART_LIMIT.
   * Ignored while a breakdown is bucketed — see resolveChartLimit.
   */
  chartLimit?: number | null
  filters: WidgetFilter[]
  breakdownBuckets?: Record<string, BreakdownBucketEntry>
}

// Read priority: breakdowns (canonical, honoured even when empty) → legacy single breakdown → none.
export const resolveBreakdowns = (config: Pick<WidgetConfig, 'breakdowns' | 'breakdown'>): string[] =>
  config.breakdowns ?? (config.breakdown != null ? [config.breakdown] : [])

// Render-time only; the resolved value is never written back. A bucketed axis is a contiguous numeric
// or date range rather than a rankable set, so no limit applies to it and a stored one is ignored: ranking
// buckets by measure and keeping the top N would leave gaps where the range has to be unbroken.
// Infinity is the encoding the tables already read to mean "unranked" — see BreakdownColumnHeader.
export const resolveChartLimit = (config: Pick<WidgetConfig, 'chartLimit'>, { bucketed }: { bucketed: boolean }): number =>
  bucketed ? Number.POSITIVE_INFINITY : (config.chartLimit ?? DEFAULT_CHART_LIMIT)
